// PlanningAIActionDetailer.js - Module dédié à la génération d'actions détaillées par IA
import React, { useState } from 'react';
import { X } from 'lucide-react';
import { askGPT } from '../components/OpenAIService';

/**
 * Hook personnalisé pour gérer la génération d'actions détaillées par IA
 */
export const usePlanningAIActionDetailer = ({
  planning,
  currentWeek,
  maxWeeksGenerated,
  setPlanning,
  setMaxWeeksGenerated,
  savePlanning,
  getDateForDay,
  isDateInPast,
  getWeekStartDate
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [detailProposals, setDetailProposals] = useState(null);
  const [showValidationModal, setShowValidationModal] = useState(false);

  /**
   * Génère des actions détaillées à partir d'une action de base
   */
  const generateDetailedActions = async (action) => {
    if (!action) return;

    setIsGenerating(true);

    try {
      const analysisPrompt = `Analyse cette action et détermine combien d'actions détaillées (entre 2 et 5) sont nécessaires selon sa complexité :

ACTION : "${action.title}"
DESCRIPTION : "${action.description}"

Critères d'évaluation :
- Simple (2 actions) : Tâche directe, peu d'étapes
- Modérée (3 actions) : Tâche avec quelques sous-étapes 
- Complexe (4 actions) : Tâche multi-facettes avec recherche + analyse + exécution
- Très complexe (5 actions) : Tâche nécessitant planification, recherche approfondie, tests, etc.

Réponds au format JSON :
{
  "complexity": "simple|moderate|complex|very_complex",
  "recommended_actions": 2-5,
  "reasoning": "Explication de pourquoi ce nombre d'actions est approprié",
  "actions": [
    {"title": "Action concrète 1", "description": "Description détaillée", "duration": "30-90 minutes", "type": "research|analysis|execution|planning|validation"},
    ...
  ]
}`;

      const response = await askGPT(analysisPrompt);
      const cleanResponse = response.replace(/```json|```/g, '').trim();
      const analysisData = JSON.parse(cleanResponse);
      
      if (analysisData.actions?.length >= 2 && analysisData.actions?.length <= 5) {
        setDetailProposals({
          baseAction: action,
          complexity: analysisData.complexity,
          reasoning: analysisData.reasoning,
          recommendedCount: analysisData.recommended_actions,
          proposedActions: analysisData.actions.map((detailAction, index) => ({
            ...detailAction,
            id: `temp_${action.id}_${index}`,
            selected: true
          }))
        });
        setShowValidationModal(true);
      } else {
        throw new Error('Nombre d\'actions invalide généré par l\'IA');
      }
    } catch (error) {
      console.error('Erreur génération actions détaillées:', error);
      alert('Erreur lors de la génération des actions détaillées. Veuillez réessayer.');
    } finally {
      setIsGenerating(false);
    }
  };

  /**
   * Valide et enregistre les actions détaillées sélectionnées
   */
  const validateDetailedActions = async () => {
    if (!detailProposals) return;

    const { baseAction, proposedActions } = detailProposals;
    const selectedActions = proposedActions.filter(action => action.selected);
    
    if (selectedActions.length === 0) {
      alert('Veuillez sélectionner au moins une action à conserver.');
      return;
    }

    try {
      const updatedPlanning = [...planning];
      
      // Trouver et supprimer l'action de base
      for (let weekIndex = 0; weekIndex < updatedPlanning.length; weekIndex++) {
        const weekData = updatedPlanning[weekIndex];
        if (weekData?.elementaryActions) {
          const actionIndex = weekData.elementaryActions.findIndex(a => a.id === baseAction.id);
          if (actionIndex !== -1) {
            weekData.elementaryActions = weekData.elementaryActions.filter(a => a.id !== baseAction.id);
            break;
          }
        }
      }
      
      const dayNames = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
      
      const today = new Date();
      let currentTargetWeek = currentWeek;
      let dayIndex = dayNames.indexOf(baseAction.day);
      
      let startDate = getDateForDay(dayIndex, currentTargetWeek);
      if (isDateInPast(startDate)) {
        startDate = new Date(today);
        startDate.setDate(today.getDate() + 1);
        dayIndex = startDate.getDay() === 0 ? 6 : startDate.getDay() - 1;
        
        const daysDiff = Math.floor((startDate - getWeekStartDate(0)) / (1000 * 60 * 60 * 24));
        currentTargetWeek = Math.floor(daysDiff / 7);
        
        while (currentTargetWeek >= updatedPlanning.length) {
          const newWeekNumber = updatedPlanning.length + 1;
          updatedPlanning.push({
            week: newWeekNumber,
            theme: `Exécution du plan d'action - Semaine ${newWeekNumber}`,
            elementaryActions: [],
            createdAt: new Date().toISOString()
          });
        }
      }
      
      selectedActions.forEach((detailAction, index) => {
        const targetDate = new Date(startDate);
        targetDate.setDate(startDate.getDate() + index);
        
        const targetDayIndex = targetDate.getDay() === 0 ? 6 : targetDate.getDay() - 1;
        const targetDay = dayNames[targetDayIndex];
        
        const daysDiff = Math.floor((targetDate - getWeekStartDate(0)) / (1000 * 60 * 60 * 24));
        const targetWeekIndex = Math.floor(daysDiff / 7);
        
        while (targetWeekIndex >= updatedPlanning.length) {
          const newWeekNumber = updatedPlanning.length + 1;
          updatedPlanning.push({
            week: newWeekNumber,
            theme: `Exécution du plan d'action - Semaine ${newWeekNumber}`,
            elementaryActions: [],
            createdAt: new Date().toISOString()
          });
        }
        
        const targetWeekData = updatedPlanning[targetWeekIndex];
        
        targetWeekData.elementaryActions.push({
          id: `detailed_${baseAction.id}_${Date.now()}_${index}`,
          stepId: baseAction.stepId,
          stepTitle: baseAction.stepTitle,
          title: detailAction.title,
          description: detailAction.description,
          duration: detailAction.duration,
          priority: index === 0 ? 'high' : 'medium',
          type: detailAction.type,
          day: targetDay,
          date: targetDate.toISOString().split('T')[0],
          timeSlot: `${9 + index * 2}:00-${10 + index * 2}:00`,
          completed: false,
          deliverable: `${detailAction.title}`,
          isElementary: true,
          isDetailed: true,
          parentActionId: baseAction.id,
          createdAt: new Date().toISOString()
        });
      }); 
      
      setMaxWeeksGenerated(Math.max(maxWeeksGenerated, updatedPlanning.length));
      
      await savePlanning(updatedPlanning);
      
      setShowValidationModal(false);
      setDetailProposals(null);
    } catch (error) {
      console.error('Erreur validation actions détaillées:', error);
      alert('Erreur lors de la validation. Veuillez réessayer.');
    }
  };

  /**
   * Annule la génération en cours
   */
  const cancelDetailGeneration = () => {
    setShowValidationModal(false);
    setDetailProposals(null);
  };

  /**
   * Toggle la sélection d'une action proposée
   */
  const toggleActionSelection = (index) => {
    if (!detailProposals) return;
    
    const updated = { ...detailProposals };
    updated.proposedActions[index].selected = !updated.proposedActions[index].selected;
    setDetailProposals(updated);
  };

  return {
    isGenerating,
    detailProposals,
    showValidationModal,
    generateDetailedActions,
    validateDetailedActions,
    cancelDetailGeneration,
    toggleActionSelection
  };
};

/**
 * Composant Modal de validation des actions détaillées
 */
export const PlanningAIActionDetailerModal = ({
  detailProposals,
  onValidate,
  onCancel,
  onToggleSelection
}) => {
  if (!detailProposals) return null;

  const selectedCount = detailProposals.proposedActions.filter(a => a.selected).length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6 shadow-xl">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">
          Valider les actions détaillées
        </h3>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h4 className="font-medium text-blue-800 mb-2">
            Analyse de complexité par l'IA
          </h4>
          <p className="text-sm text-blue-700">
            <strong>Action originale :</strong> "{detailProposals.baseAction.title}"
          </p>
          <p className="text-sm text-blue-700 mb-2">
            <strong>Complexité détectée :</strong> {
              detailProposals.complexity === 'simple' ? 'Simple' :
              detailProposals.complexity === 'moderate' ? 'Modérée' :
              detailProposals.complexity === 'complex' ? 'Complexe' :
              'Très complexe'
            }
          </p>
          <p className="text-sm text-blue-700 mb-2">
            <strong>Actions recommandées :</strong> {detailProposals.recommendedCount}
          </p>
          <p className="text-sm text-blue-700">
            <strong>Justification :</strong> {detailProposals.reasoning}
          </p>
        </div>

        <p className="text-gray-600 mb-4">
          L'IA a analysé votre action et propose {detailProposals.proposedActions.length} actions détaillées. 
          Sélectionnez celles que vous souhaitez conserver :
        </p>
        
        <div className="space-y-3 mb-6">
          {detailProposals.proposedActions.map((action, index) => (
            <div key={action.id} className={`border rounded-lg p-4 transition-all ${
              action.selected ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'
            }`}>
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  checked={action.selected}
                  onChange={() => onToggleSelection(index)}
                  className="mt-1 h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <h4 className="font-medium text-gray-800">{action.title}</h4>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      action.type === 'research' ? 'bg-blue-100 text-blue-700' :
                      action.type === 'analysis' ? 'bg-purple-100 text-purple-700' :
                      action.type === 'execution' ? 'bg-green-100 text-green-700' :
                      action.type === 'planning' ? 'bg-orange-100 text-orange-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {action.type}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{action.description}</p>
                  <div className="flex items-center space-x-4 text-xs text-gray-500">
                    <span>{action.duration}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-500">
            {selectedCount} action(s) sélectionnée(s)
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={onValidate}
              disabled={selectedCount === 0}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Valider les actions sélectionnées
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default { usePlanningAIActionDetailer, PlanningAIActionDetailerModal };